import { useState, useEffect, useMemo } from 'react';
import * as tnaServices from '../services/tnaServices';


/**
 * Hook that computes the difference between two TNAs (Training Needs Analyses).
 *
 * Given a pair of TNA IDs (tnaFromId / tnaToId from moduleDetails) and the full
 * list of ATA chapters, it:
 *   1. Fetches both TNA records from the API.
 *   2. Fetches all modules that belong to each TNA.
 *   3. Builds a consolidated row per ATA chapter containing the "from" and "to"
 *      values for components, objectives, and training level, ready for display
 *      in a side-by-side diff table.
 *
 * @param {object} moduleDetails   - Object with tnaFromId and tnaToId properties.
 * @param {Array}  allAtas         - Full list of ATA chapter objects ({ ataId, ataNo, … }).
 * @returns {{ consolidated: Array, consolidatedById: Map }}
 *   consolidated    – One row per ATA chapter with from/to fields populated.
 *   consolidatedById – The same rows keyed by ataId for O(1) lookups.
 */

function useTnaDiff(moduleDetails, allAtas) {
  // The two TNA header records fetched from the API
  const [tnaFrom, setTnaFrom] = useState(null);
  const [tnaTo, setTnaTo] = useState(null);

  // Full module detail arrays for each TNA side (each entry is the API response for one module)
  const [tnaFromModules, setTnaFromModules] = useState([]);
  const [tnaToModules, setTnaToModules] = useState([]);

  // Final per-ATA diff rows exposed to consumers
  const [consolidated, setConsolidated] = useState([]);

  // Step 1 – fetch the two TNA header records whenever the IDs change.
  // Both requests are fired in parallel; the effect is aborted on cleanup to
  // prevent stale state updates if the component unmounts or IDs change.
  useEffect(() => {
    const { tnaFromId, tnaToId } = moduleDetails || {};
    if (!tnaFromId || !tnaToId) {
      // Clear state when either ID is missing (e.g. no diff selected yet)
      setTnaFrom(null); setTnaTo(null);
      return;
    }
    let ac = new AbortController();
    (async () => {
      try {
        const [fromRes, toRes] = await Promise.all([
          tnaServices.getById(tnaFromId),
          tnaServices.getById(tnaToId),
        ]);
        if (ac.signal.aborted) return;
        setTnaFrom(fromRes.data?.[0] || null);
        setTnaTo(toRes.data?.[0] || null);
      } catch (e) {
        if (!ac.signal.aborted) console.error('Error fetching TNA difference details:', e);
      }
    })();
    return () => ac.abort();
  }, [moduleDetails?.tnaFromId, moduleDetails?.tnaToId, moduleDetails]);

  // Step 2 – once the TNA headers are available, fetch the full detail for
  // every module listed inside each TNA. Both sides are loaded concurrently.
  // A shared AbortController cancels both if the effect re-runs.
  useEffect(() => {
    let ac = new AbortController();

    // Fetches all modules for one TNA side and stores them via the provided setter.
    // Each module is fetched individually because the list only contains IDs.
    const loadSide = async (tna, setter) => {
      if (!tna?.modules?.length) { setter([]); return; }
      try {
        const res = await Promise.all(
          tna.modules.map(m => tnaServices.getModuleById(m.tnaModuleId))
        );
        if (!ac.signal.aborted) setter(res.map(r => r.data));
      } catch (e) {
        if (!ac.signal.aborted) console.error('Error fetching TNA modules:', e);
      }
    };

    if (tnaFrom) loadSide(tnaFrom, setTnaFromModules);
    if (tnaTo) loadSide(tnaTo, setTnaToModules);
    return () => ac.abort();
  }, [tnaFrom, tnaTo]);

  // Step 3 – build the consolidated diff rows whenever modules or ATAs change.
  // One row is produced per ATA chapter, containing the "from" and "to" values
  // for components, objectives, and level sourced from the loaded modules.
  useEffect(() => {
    if (!allAtas?.length) { setConsolidated([]); return; }

    /**
     * Extracts a field value for a given ATA chapter across all modules of one TNA side.
     *
     * - For "level": returns the highest numeric value found across all modules
     *   (a module may repeat an ATA at different levels; the max is used).
     * - For "components" / "objectives": concatenates all non-empty values with
     *   a newline separator so every module's text is preserved.
     *
     * @param {Array}  tnaModules - Loaded module details for one side (from or to).
     * @param {number} ataId      - The ATA chapter to look up within each module.
     * @param {string} field      - The field to extract: 'level', 'components', or 'objectives'.
     * @returns {string}
     */
    const pick = (tnaModules, ataId, field) => {
      if (!tnaModules?.length) return '';
      const values = tnaModules.map(mod => {
        // Each module response is an array; grab the first element's ATA list
        const element = mod?.[0]?.ata?.find(a => a.ataId === ataId);
        return element?.[field];
      }).filter(Boolean);

      if (field === 'level') {
        // Use the highest level found across all modules for this ATA chapter
        const nums = values.map(n => Number(n)).filter(n => !Number.isNaN(n));
        return nums.length ? String(Math.max(...nums)) : '';
      }
      // For text fields, join each module's value on a new line
      return values.join('\n');
    };

    // Map every ATA chapter to a diff row with from/to values for all three fields
    const rows = allAtas.map(ata => ({
      ataId: ata.ataId,
      ataNo: ata.ataNo,
      fromComponents: pick(tnaFromModules, ata.ataId, 'components'),
      toComponents:   pick(tnaToModules,   ata.ataId, 'components'),
      fromObjectives: pick(tnaFromModules, ata.ataId, 'objectives'),
      toObjectives:   pick(tnaToModules,   ata.ataId, 'objectives'),
      maxFromLevel:   pick(tnaFromModules, ata.ataId, 'level'),
      maxToLevel:     pick(tnaToModules,   ata.ataId, 'level'),
    }));
    setConsolidated(rows);
  }, [tnaFromModules, tnaToModules, allAtas]);

  // Derived index: Map<ataId, row> built from the consolidated array.
  // Lets consumers look up a specific ATA's diff data in O(1) instead of scanning the array.
  const consolidatedById = useMemo(() => {
    const m = new Map();
    consolidated.forEach(r => m.set(r.ataId, r));
    return m;
  }, [consolidated]);

  return { consolidated, consolidatedById };
}

export default useTnaDiff;